import { Controller } from "@hotwired/stimulus";
import toast from "../utils/toast";

export default class extends Controller {
  static targets = ["amountInput"];
  static values = {
    maxCh: Number,       // e.g. 10
    padCh: Number,       // e.g. 1
    defaultValue: String, // optional fallback if you ever want one
    autoFocus: { type: Boolean, default: true }, // control auto-focus behavior
  };

  connect() {
    this.inIframe = window.self !== window.top;

    // Use placeholder "0" when empty (no default value set)
    if (!this.element.placeholder) this.element.placeholder = "0";
    // if (!this.element.value) this.element.value = this.setAmountValue;

    // viewport cap + initial size
    this.element.style.maxWidth = "90vw";
    this._autosize();

    // focus fallback (works in modals/frames) - only if autoFocus is enabled
    if (this.autoFocusValue) {
      requestAnimationFrame(() => {
        try { this.element.focus({ preventScroll: true }); } catch {}
      });
    }

    this.amountInputTarget.addEventListener("input", this.handleInput.bind(this));

    this.element.addEventListener("input", () => this._autosize());
    this.element.addEventListener("blur", () => this._onBlur());
    window.addEventListener("resize", this._onResize);
  }

  disconnect() {
    window.removeEventListener("resize", this._onResize);
  }



  _onResize = () => this._autosize();

  _autosize() {
    // Use placeholder when empty so width doesn't collapse
    const raw = this.element.value ?? "";
    const base = raw === "" ? (this.element.placeholder || "") : raw;

    const pad   = this.hasPadChValue ? this.padChValue : 1;
    const maxCh = this.hasMaxChValue ? this.maxChValue : 10;

    // Treat periods as ~half a character for visual centering
    const dotCount     = (base.match(/\./g) || []).length;
    const effectiveLen = base.length - dotCount * 0.5;

    const ch = Math.min(maxCh, Math.max(1, effectiveLen + pad));
    this.element.style.width = `${ch}ch`;
  }

  _onBlur() {
    if (this.element.hasAttribute("readonly")) return;

    let raw = (this.element.value || "").trim();

    // Try backend-provided min, fallback 0
    let min = Number(this.minValue ?? 0);

    // Try HTML min attribute
    const minAttr = Number.parseFloat(this.element.getAttribute("min"));
    if (!Number.isNaN(minAttr)) {
      min = minAttr;
    }
    
    let val = Number.parseFloat(raw);
    if (Number.isNaN(val)) {
      this._autosize();
      return;
    }

    // min value needs to be converted to float
    min = min.toFixed(2)

    if (val < min) {
      toast(`Amount cannot be less than ${min}`, "error");
      this.element.value = min;
      this._autosize();

      if (this.inIframe) {
        window.parent.postMessage({
          type: "transaction_error",
          message: `Amount cannot be less than ${min}`
        }, "*");
      }
      return;
    }

    const maxAttr = this.element.getAttribute("max");
    const max = Number.parseFloat(maxAttr);

    if (!Number.isNaN(max) && val > max) {
      val = max;
      toast(`Amount cannot be greater than ${max}`, "error");
      if (this.inIframe) {
        window.parent.postMessage({
          type: "transaction_error",
          message: `Amount cannot be greater than ${max}`
        }, "*");
      }
    }

    this.element.value = val.toFixed(2);
    this._autosize();
  }



  handleInput(e) {
    const input = e.currentTarget;
    let value = input.value;

    // Only allow numbers and one decimal point
    value = value.replace(/[^0-9.]/g, '');

    // Ensure only one decimal point
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }

    // Update the input value
    input.value = value;
  }
}